import { Button } from '@radix-ui/themes'
import NavMenu from '../nav-menu/NavMenu'
import { logoutUser, selectUser, setDarkMode } from '../../reducers/users'
import { useAppDispatch } from '../../reducers/store'
import { useNavigate } from 'react-router-dom'
import {
  IconBook2,
  IconDoorExit,
  IconHelp,
  IconMoon,
  IconNotebook,
  IconRefresh,
  IconSettings,
  IconSun,
} from '@tabler/icons-react'
import { useDispatch, useSelector } from 'react-redux'
import { RoleType } from '../../types/UserTypes'
import { getRoles, permitsRole } from '../../utils/roles'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { resetDemoAccount } from '../../reducers/settings'
import toast from 'react-hot-toast'
import { inPov } from '../../utils/session'
import { DEFAULT_TOAST_CONFIG } from '../toast/consts'

function SideBar() {
  const di = useAppDispatch()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { session, darkModeEnabled } = useSelector(selectUser)
  const roles = getRoles(session)
  const { darkMode } = useFlags()

  const logout = async () => {
    await di(logoutUser())
    navigate('/login')
  }

  const handleDarkMode = (e: boolean) => {
    document.cookie = `DARK_MODE=${e}; path=/; max-age=31536000`
    dispatch(setDarkMode(e))
  }

  const demoAccount = () => {
    return session?.user?.email === 'demo@acme-inc.io'
  }

  const resetAccount = async () => {
    const reset = await di(resetDemoAccount())
    if (reset) {
      await di(logoutUser())
      navigate('/login')
    } else {
      toast.error('Failed to reset account, please try again', DEFAULT_TOAST_CONFIG)
    }
  }

  return (
    <>
      <div
        className="w-[208px] overflow-y-auto overflow-x-hidden flex flex-col py-5"
        style={{
          height: `calc(100vh - ${inPov(session) ? '116px' : '74px'})`,
        }}
      >
        {/* NAV MENU */}
        <NavMenu />

        {/* DARK MODE TOGGLE */}
        {darkMode && (
          <>
            <hr className="my-2 border-t border-gray-300 dark:border-lightViolet" />
            <div className="mx-8 cursor-pointer">
              <div className="flex w-full h-10 bg-gray-300 rounded-full border-2 border-gray-300 dark:border-lightViolet dark:bg-lightViolet">
                <div
                  className={`w-1/2 h-full ${darkModeEnabled ? 'bg-lightViolet' : 'bg-mainBackground'} flex items-center justify-center rounded-full text-mainText`}
                  onClick={() => handleDarkMode(false)}
                >
                  <IconSun size={18} />
                </div>

                <div
                  className={`w-1/2 h-full ${darkModeEnabled ? 'bg-mainBackground' : 'bg-gray-300'} flex items-center justify-center rounded-full text-mainText`}
                  onClick={() => handleDarkMode(true)}
                >
                  <IconMoon size={18} />
                </div>
              </div>
            </div>
          </>
        )}
        <hr className="my-2 border-t border-gray-300 dark:border-lightViolet" />

        {/* BOTTOM NAV */}
        <div className="mx-4">
          <Button
            variant="solid"
            size="3"
            className="text-mainText text-[14px] font-medium bg-mainBackground w-[182px] h-10 justify-start cursor-pointer mb-1"
            onClick={() => window.open('https://portal.usepylon.com/iverify/forms/support-ticket-form', '_blank')}
          >
            <IconHelp size="18" /> Support
          </Button>
          <Button
            variant="solid"
            size="3"
            className="text-mainText text-[14px] font-medium bg-mainBackground w-[182px] h-10 justify-start cursor-pointer mb-1"
            onClick={() => window.open(`${window.location.origin}/docs/iVerify-release-notes/`, '_blank')}
          >
            <IconNotebook size="18" /> Release Notes
          </Button>
          <Button
            variant="solid"
            size="3"
            className="text-mainText text-[14px] font-medium bg-mainBackground w-[182px] h-10 justify-start cursor-pointer mb-1"
            onClick={() => {
              window.open(`${window.location.origin}/docs/intro`, '_blank', 'noopener,noreferrer')
            }}
          >
            <IconBook2 size="18" /> Documentation
          </Button>
          {roles && permitsRole(roles, [RoleType.PLATFORM_MGMT, RoleType.ACCESS_MGMT]) && (
            <Button
              variant="solid"
              size="3"
              className="text-mainText text-[14px] font-medium bg-mainBackground w-[182px] h-10 justify-start cursor-pointer mb-1"
              onClick={() => navigate('/settings')}
            >
              <IconSettings size="18" /> Settings
            </Button>
          )}
          <Button
            variant="solid"
            size="3"
            className="text-mainText text-[14px] font-medium bg-mainBackground w-[182px] h-10 justify-start cursor-pointer"
            onClick={() => logout()}
          >
            <IconDoorExit size="18" /> Sign Out
          </Button>
          {demoAccount() && (
            <Button
              variant="solid"
              size="3"
              className="text-mainText text-[14px] font-medium bg-mainBackground w-[182px] h-10 justify-start cursor-pointer mb-1"
              onClick={() => resetAccount()}
            >
              <IconRefresh size="18" /> Reset Account
            </Button>
          )}
        </div>
      </div>
    </>
  )
}

export default SideBar
